import React, { useState } from "react";
import api, { apiError } from "@/lib/api";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectSeparator, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Plus, X, Pencil, Trash2, Check } from "lucide-react";

const NONE = "__none__", NEW = "__new__", MANAGE = "__manage__";

function MyCategoryRow({ c, setCats }) {
  const [edit, setEdit] = useState(false);
  const [label, setLabel] = useState(c.label);
  const save = async () => {
    try { const { data } = await api.patch(`/task-categories/personal/${c.id}`, { label }); setCats((p) => p.map((x) => (x.id === c.id ? { ...x, ...data } : x))); setEdit(false); toast.success("Category renamed"); }
    catch (e) { toast.error(apiError(e)); }
  };
  const remove = async () => {
    try { await api.delete(`/task-categories/personal/${c.id}`); setCats((p) => p.map((x) => (x.id === c.id ? { ...x, active: false } : x))); toast.success(`Removed "${c.label}"`); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="flex items-center gap-2 py-2 border-b border-slate-100 last:border-0" data-testid={`my-category-${c.id}`}>
      {edit ? <Input autoFocus value={label} maxLength={40} onChange={(e) => setLabel(e.target.value)} className="h-8 text-sm"
        onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") setEdit(false); }} data-testid={`my-category-input-${c.id}`} />
        : <span className="flex-1 text-sm text-slate-700 truncate">{c.label}</span>}
      {edit ? <Button size="icon" variant="ghost" className="h-8 w-8" onClick={save} aria-label="Save name" data-testid={`my-category-save-${c.id}`}><Check className="w-4 h-4 text-emerald-600" /></Button>
        : <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setEdit(true)} aria-label="Rename" data-testid={`my-category-rename-${c.id}`}><Pencil className="w-4 h-4 text-slate-500" /></Button>}
      <Button size="icon" variant="ghost" className="h-8 w-8" onClick={remove} aria-label="Remove" data-testid={`my-category-remove-${c.id}`}><Trash2 className="w-4 h-4 text-rose-500" /></Button>
    </div>
  );
}

export function MyCategoriesDialog({ open, onClose, cats, setCats }) {
  const mine = cats.filter((c) => c.personal && c.active !== false);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-sm" data-testid="my-categories-dialog">
        <DialogHeader><DialogTitle>My categories</DialogTitle>
          <DialogDescription>Only you see these. Removing one keeps it on tasks that already use it.</DialogDescription></DialogHeader>
        {mine.length ? <div>{mine.map((c) => <MyCategoryRow key={c.id} c={c} setCats={setCats} />)}</div>
          : <p className="text-sm text-slate-500" data-testid="my-categories-empty">You haven't created any personal categories yet.</p>}
      </DialogContent>
    </Dialog>
  );
}

// Shared + personal task categories, with inline "New personal category".
export function CategorySelect({ value, onChange, cats, setCats, testId = "task-category-select", className }) {
  const [adding, setAdding] = useState(false);
  const [manage, setManage] = useState(false);
  const [label, setLabel] = useState("");
  const active = cats.filter((c) => c.active !== false || c.key === value);
  const shared = active.filter((c) => !c.personal), mine = active.filter((c) => c.personal);
  const add = async () => {
    if (!label.trim()) return;
    try {
      const { data } = await api.post("/task-categories/personal", { label: label.trim() });
      setCats((p) => (p.some((c) => c.key === data.key) ? p : [...p, data]));
      onChange(data.key); setAdding(false); setLabel("");
      toast.success(`Personal category "${data.label}" added`);
    } catch (e) { toast.error(apiError(e)); }
  };
  if (adding) {
    return (
      <div className="flex gap-1" data-testid="personal-category-add">
        <Input autoFocus value={label} onChange={(e) => setLabel(e.target.value)} maxLength={40} placeholder="My category name" className={className}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } if (e.key === "Escape") setAdding(false); }} data-testid="personal-category-input" />
        <Button type="button" size="icon" variant="outline" className="shrink-0" onClick={add} aria-label="Add category" data-testid="personal-category-save"><Plus className="w-4 h-4" /></Button>
        <Button type="button" size="icon" variant="ghost" className="shrink-0" onClick={() => setAdding(false)} aria-label="Cancel" data-testid="personal-category-cancel"><X className="w-4 h-4" /></Button>
      </div>
    );
  }
  return (
    <>
    <Select value={value || NONE} onValueChange={(v) => (v === NEW ? setAdding(true) : v === MANAGE ? setManage(true) : onChange(v === NONE ? null : v))}>
      <SelectTrigger className={className} data-testid={testId}><SelectValue placeholder="None" /></SelectTrigger>
      <SelectContent className="bg-white max-h-72">
        <SelectItem value={NONE}>None</SelectItem>
        {shared.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
        {mine.length > 0 && <SelectGroup><SelectSeparator /><SelectLabel className="text-[11px] text-slate-400">My categories</SelectLabel>
          {mine.map((c) => <SelectItem key={c.key} value={c.key} data-testid={`personal-category-${c.key}`}>{c.label}</SelectItem>)}</SelectGroup>}
        <SelectSeparator />
        <SelectItem value={NEW} className="text-brand font-medium" data-testid="personal-category-new">+ New personal category…</SelectItem>
        {mine.length > 0 && <SelectItem value={MANAGE} className="text-slate-600" data-testid="personal-category-manage">Manage my categories…</SelectItem>}
      </SelectContent>
    </Select>
    <MyCategoriesDialog open={manage} onClose={() => setManage(false)} cats={cats} setCats={setCats} />
    </>
  );
}
